import React, { useEffect, useMemo, useState } from 'react';

function formatCurrency(value, currency = 'CAD') {
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toFixed(2)}`;
  }
}

function formatDate(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const res = await fetch('/api/products', { cache: 'no-store' });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const items = Array.isArray(data.products) ? data.products : [];
        setProducts(items);
      } catch (e) {
        setError(e.message || 'Unable to load catalog');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const catalogSummary = useMemo(() => {
    const productCount = products.length;
    const totalValue = products.reduce((sum, product) => sum + Number(product.price || 0), 0);
    const avgPrice = productCount ? totalValue / productCount : 0;
    const currencyList = Array.from(new Set(products.map((product) => product.currency).filter(Boolean)));

    return {
      productCount,
      avgPrice,
      currencies: currencyList.length ? currencyList.join(', ') : 'CAD',
    };
  }, [products]);

  if (loading) {
    return (
      <section className="admin-view admin-view--catalog">
        <div className="section-card stock-loading">Loading catalog...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="admin-view admin-view--catalog">
        <div className="section-card stock-error">Error: {error}</div>
      </section>
    );
  }

  return (
    <section className="admin-view admin-view--catalog">
      <div className="catalog-panel section-card">
        <div>
          <span className="eyebrow">Catalog</span>
          <h2>Products</h2>
          <p>Connected to the live `/api/products` feed.</p>
        </div>

        <div className="catalog-panel__stats">
          <div>
            <span>Products</span>
            <strong>{catalogSummary.productCount}</strong>
          </div>
          <div>
            <span>Average price</span>
            <strong>{formatCurrency(catalogSummary.avgPrice, 'CAD')}</strong>
          </div>
          <div>
            <span>Currencies</span>
            <strong>{catalogSummary.currencies}</strong>
          </div>
        </div>
      </div>

      <div className="catalog-table-card section-card">
        <div className="catalog-table-card__header">
          <div>
            <span className="eyebrow">Feed</span>
            <h3>Current catalog list</h3>
          </div>
        </div>

        {products.length ? (
          <div className="catalog-table-wrap">
            <table className="catalog-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id || product.sku || product.name}>
                    <td>{product.sku || 'N/A'}</td>
                    <td>
                      <div className="catalog-product-cell">
                        <strong>{product.name || 'Untitled product'}</strong>
                        <span>{product.description || 'No description'}</span>
                      </div>
                    </td>
                    <td>{product.category || product.metadata?.category || 'General'}</td>
                    <td>{formatCurrency(product.price, product.currency || 'CAD')}</td>
                    <td>{formatDate(product.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="stock-empty">No products found.</p>
        )}
      </div>
    </section>
  );
}
